import { SnackbarAction, SnackbarActionConfiguration, SnackbarActionType, SnackbarConfiguration } from '../../models';
import { isPrimary } from '../color/snackbar-color.helper';
import { parseIconConfiguration } from '../icon/snackbar-icon.helper';

/**
 * Validate, extract and parse a valid snackbar action from an action configuration
 * @param {SnackbarActionConfiguration} action - action to parse
 *
 * @throws when no callback set on an action
 * @returns {(SnackbarAction | undefined)} parsed action if valid action, undefined if no action
 */
export const parseAction = (action: SnackbarActionConfiguration | undefined): SnackbarAction | undefined => {
  if (!action) {
    return undefined;
  }

  if (!action.callback) {
    throw new Error('[Snackbar] Cannot create action without a callback');
  }

  const defaultActionConfiguration: Pick<SnackbarActionType, 'text' | 'color'> = {
    text: '',
    color: 'primary',
  };

  return {
    ...action,
    icon: parseIconConfiguration(action.icon),
    text: action.text || defaultActionConfiguration.text,
    color: action.color || defaultActionConfiguration.color,
  };
};

/**
 * Extract and parse actions from snackbar configuration
 *
 * Note: actions are not allowed in "primary" color
 * @param {SnackbarConfiguration} configuration - configuration to parse
 *
 * @returns {({ left: SnackbarAction, right: SnackbarAction })} a mapped object with left and right actions.
 *
 * Note: For primary color, actions are undefined in mapped object
 */
export const getActions = (configuration: SnackbarConfiguration): { left: SnackbarAction | undefined, right: SnackbarAction | undefined } => {
  if (isPrimary(configuration)) {
    if (configuration.actions) {
      console.warn('[Snackbar] Actions are unavailable for "primary" color, configuration will be ignored');
    }

    return { left: undefined, right: undefined };
  }

  return {
    left: parseAction(configuration.actions?.left),
    right: parseAction(configuration.actions?.right),
  };
};
